// Liste des attributions d'emplacements : en vigueur ou non, à renouveler sous 30 jours, par état,
// recherche sur le numéro, le commerçant ou la référence ; restriction à un marché par l'adresse.
import { ETATS_ATTRIBUTION } from '@cc/contrats';
import { Bouton, Champ, EtatVide, Filtres, Lien, LienBouton, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { Plus, Store } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useAttributions, useMarche } from './api';
import { LIBELLES_CANAL, LIBELLES_ETAT_ATTRIBUTION, LIBELLES_PERIODICITE, LIBELLES_SITUATION, libelleCommercant } from './logique';
import type { Attribution, PeriodiciteRedevance } from './types';
import commun from '../../commun/commun.module.css';

export const TON_SITUATION: Record<string, 'ok' | 'attente' | 'retard' | 'neutre'> = { en_cours: 'ok', a_venir: 'attente', echue: 'neutre', resiliee: 'retard', terminee: 'neutre' };

export function PageAttributions() {
  const { contexte } = useSession();
  const [params] = useSearchParams();
  const marcheId = params.get('marche');
  const marche = useMarche(marcheId);
  const [filtres, setFiltres] = useFiltresPersistes('attributions', { vigueur: 'oui', renouveler: '', etat: '' });
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const parametres: Record<string, string> = {};
  if (marcheId) parametres.marche = marcheId;
  if (filtres.vigueur) parametres.en_vigueur = filtres.vigueur;
  if (filtres.renouveler) parametres.a_renouveler = filtres.renouveler;
  if (filtres.etat) parametres.etat = filtres.etat;
  if (recherche) parametres.q = recherche;
  const liste = useAttributions(parametres);
  const colonnes: ColonneTableau<Attribution>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (a) => a.numero, cellule: (a) => <Lien href={`/marches/attributions/${a.id}`} className={commun.reference}>{a.numero}</Lien> },
    { id: 'emplacement', entete: 'Emplacement', cellule: (a) => `${a.emplacement.reference ?? ''}${a.marche?.nom ? ` · ${a.marche.nom}` : ''}`, valeurTri: (a) => a.emplacement.reference },
    { id: 'commercant', entete: 'Commerçant', cellule: (a) => libelleCommercant(a), valeurTri: (a) => a.commercant.nom },
    { id: 'periode', entete: 'Période', cellule: (a) => `${date(a.periode.debut)} au ${date(a.periode.fin)}`, valeurTri: (a) => a.periode.debut },
    { id: 'periodicite', entete: 'Redevance', cellule: (a) => LIBELLES_PERIODICITE[a.periodicite as PeriodiciteRedevance] ?? a.periodicite },
    { id: 'canal', entete: 'Origine', cellule: (a) => LIBELLES_CANAL[a.canal] ?? a.canal },
    {
      id: 'situation',
      entete: 'Situation',
      cellule: (a) => (
        <>
          <PuceEtat ton={TON_SITUATION[a.situation] ?? 'neutre'} libelle={LIBELLES_SITUATION[a.situation]} />
          {a.renouvellement_propose_le ? <span className={commun.na}> renouvellement proposé</span> : null}
        </>
      ),
      valeurTri: (a) => a.situation,
    },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Marchés municipaux"
        titre={marche.data ? `Attributions · ${marche.data.nom}` : 'Attributions'}
        description="Attributions d'emplacements, redevances périodiques émises par le module des recettes, résiliations et renouvellements."
        actions={
          <>
            <LienBouton href={marcheId ? `/marches/${marcheId}` : '/marches'} variante="secondaire">
              {marcheId ? 'Plan du marché' : 'Marchés'}
            </LienBouton>
            {peut(contexte, 'marche_municipal', 'creer') ? (
              <LienBouton href="/marches/attributions/nouvelle" variante="principal" icone={Plus}>
                Nouvelle attribution
              </LienBouton>
            ) : null}
          </>
        }
      />
      <Filtres
        groupes={[
          { id: 'vigueur', libelle: 'Vigueur', options: [{ valeur: '', libelle: 'Toutes' }, { valeur: 'oui', libelle: 'En vigueur' }, { valeur: 'non', libelle: 'Terminées ou résiliées' }] },
          { id: 'renouveler', libelle: 'Renouvellement', options: [{ valeur: '', libelle: 'Indifférent' }, { valeur: 'oui', libelle: 'À renouveler sous 30 jours' }] },
          { id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...ETATS_ATTRIBUTION.map((e) => ({ valeur: e, libelle: LIBELLES_ETAT_ATTRIBUTION[e] }))] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <form className={commun.ligne} style={{ alignItems: 'flex-end' }} role="search" onSubmit={(e) => { e.preventDefault(); setRecherche(q.trim()); }}>
        <Champ libelle="Rechercher (numéro, commerçant, référence)" type="search" value={q} onChange={(e) => setQ(e.target.value)} />
        <Bouton type="submit" variante="secondaire">Rechercher</Bouton>
      </form>
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L2-B" fonction="La liste des attributions">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune attribution" icone={Store}>
            <p>Aucune attribution ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Attributions" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(a) => a.id} cleColonnes="attributions" />
        )}
      </EtatRequete>
    </>
  );
}
